// Where each thought (emoji) sits in the Sky of Thoughts.
//
// The Sky is one endless field of spots. The field is a seamless tile of
// "blue noise" (random, but no two spots closer than GAP), repeated in every
// direction, so it never runs out and never looks like a grid.
//
// Home is the first screen: the spots you see on arrival, minus the ones under
// the page's controls. The newest thoughts live at home. When home is full, a
// new thought takes the spot of the oldest one at home, and that one moves out
// into the wider Sky. Nothing else moves. The wider Sky fills outward from
// home, ring by ring, in the order thoughts left home.
//
// Everything is seeded, so the same thoughts on the same screen size always
// land on the same spots, visit after visit.
//
// Units are "sky px": desktop pixels. Phones draw the Sky a little smaller
// (SkyGeometry.scale), so more fits on a small screen.

export type Pt = { x: number; y: number };
export type Rect = { x: number; y: number; w: number; h: number };
export type SkyGeometry = { w: number; h: number; scale: number; avoid: Rect[] };

/** Smallest distance between two spots, centre to centre (sky px). */
export const GAP = 72;
/** A seamless tile of the field. Big, so the repeat is never noticed. */
const TILE = 1440;
/** Keep home spots this far from the screen edges, so no emoji is cut off. */
const EDGE = 26;
/** Keep home spots this far outside a control (an emoji radius plus a little). */
const AVOID_PAD = 32;

// Small, fast seeded random numbers (mulberry32).
export const rng = (seed: number) => () => {
  seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

export const hash = (s: string) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
};

/** Emoji font size (sky px). Thoughts with replies are a little bigger. */
export const emojiSize = (size: number, replies = 0) => (size + 10) * (replies > 0 ? 1.18 : 1);
/** Rough radius of the drawn emoji glyph. */
export const emojiRadius = (size: number, replies = 0) => emojiSize(size, replies) * 0.6;

// One tile of blue noise (Bridson's Poisson disk sampling), wrapped at the
// tile edges, so copies placed side by side keep the GAP across the seams.
let tileCache: Pt[] | null = null;
function fieldTile(): Pt[] {
  if (tileCache) return tileCache;
  const rand = rng(20260924);
  const n = Math.ceil(TILE / (GAP / Math.SQRT2));
  const cell = TILE / n;
  const grid = new Int32Array(n * n).fill(-1);
  const pts: Pt[] = [];
  const wrap = (v: number) => ((v % TILE) + TILE) % TILE;
  const far = (x: number, y: number) => {
    const gx = Math.floor(x / cell), gy = Math.floor(y / cell);
    for (let j = -2; j <= 2; j++) {
      for (let i = -2; i <= 2; i++) {
        const k = grid[((gy + j + n) % n) * n + ((gx + i + n) % n)];
        if (k < 0) continue;
        let dx = Math.abs(pts[k].x - x), dy = Math.abs(pts[k].y - y);
        dx = Math.min(dx, TILE - dx); dy = Math.min(dy, TILE - dy);
        if (dx * dx + dy * dy < GAP * GAP) return false;
      }
    }
    return true;
  };
  const add = (x: number, y: number) => {
    grid[Math.floor(y / cell) * n + Math.floor(x / cell)] = pts.length;
    pts.push({ x, y });
    active.push(pts.length - 1);
  };
  const active: number[] = [];
  add(rand() * TILE, rand() * TILE);
  while (active.length) {
    const ai = Math.floor(rand() * active.length);
    const p = pts[active[ai]];
    let placed = false;
    for (let k = 0; k < 30; k++) {
      const a = rand() * Math.PI * 2, r = GAP * (1 + rand());
      const x = wrap(p.x + Math.cos(a) * r), y = wrap(p.y + Math.sin(a) * r);
      if (far(x, y)) { add(x, y); placed = true; break; }
    }
    if (!placed) active.splice(ai, 1);
  }
  return (tileCache = pts);
}

/** Every field spot inside a rectangle (sky px). */
function fieldIn(x0: number, y0: number, x1: number, y1: number): Pt[] {
  const tile = fieldTile();
  const out: Pt[] = [];
  for (let ty = Math.floor(y0 / TILE); ty <= Math.floor(y1 / TILE); ty++) {
    for (let tx = Math.floor(x0 / TILE); tx <= Math.floor(x1 / TILE); tx++) {
      for (const p of tile) {
        const x = p.x + tx * TILE, y = p.y + ty * TILE;
        if (x >= x0 && x < x1 && y >= y0 && y < y1) out.push({ x, y });
      }
    }
  }
  return out;
}

const key = (p: Pt) => `${Math.round(p.x)},${Math.round(p.y)}`;

type Where = { home: true; i: number } | { home: false; j: number };

/**
 * Places thoughts and remembers where they went, so a new thought moves at
 * most one other thought (the oldest at home) and a removed one moves none.
 */
export class SkyPlacer {
  readonly geo: SkyGeometry;
  /** Home spots, in fill order: each next spot is the one with the most room. */
  readonly home: Pt[];
  private homeKeys: Set<string>;
  private centre: Pt;
  private archive: Pt[] = [];
  private archiveR = 0;
  private occ: (string | null)[];
  private where = new Map<string, Where>();
  private seq = new Map<string, number>();
  private nextSeq = 0;
  private nextArchive = 0;
  private pending = new Set<string>();
  private started = false;

  constructor(geo: SkyGeometry) {
    this.geo = geo;
    const W = geo.w / geo.scale, H = geo.h / geo.scale;
    this.centre = { x: W / 2, y: H / 2 };
    const avoid = geo.avoid.map(a => ({ x: a.x / geo.scale, y: a.y / geo.scale, w: a.w / geo.scale, h: a.h / geo.scale }));
    const free = fieldIn(EDGE, EDGE, W - EDGE, H - EDGE).filter(p => !avoid.some(a =>
      p.x > a.x - AVOID_PAD && p.x < a.x + a.w + AVOID_PAD && p.y > a.y - AVOID_PAD && p.y < a.y + a.h + AVOID_PAD));
    this.home = spreadOrder(free, this.centre);
    this.homeKeys = new Set(this.home.map(key));
    this.occ = new Array(this.home.length).fill(null);
  }

  get capacity() { return this.home.length; }

  /** The j-th spot of the wider Sky, nearest to home first. */
  private archiveAt(j: number): Pt {
    while (this.archive.length <= j) {
      // Grow the circle until it holds enough spots. Spots inside the old
      // circle are all nearer than new ones, so earlier spots never change.
      this.archiveR = this.archiveR ? this.archiveR * 1.5 : Math.hypot(this.centre.x, this.centre.y) + TILE / 2;
      const { x: cx, y: cy } = this.centre, R = this.archiveR;
      this.archive = fieldIn(cx - R, cy - R, cx + R, cy + R)
        .filter(p => !this.homeKeys.has(key(p)) && Math.hypot(p.x - cx, p.y - cy) <= R)
        .sort((a, b) => Math.hypot(a.x - cx, a.y - cy) - Math.hypot(b.x - cx, b.y - cy));
    }
    return this.archive[j];
  }

  private spot(w: Where): Pt { return w.home ? this.home[w.i] : this.archiveAt(w.j); }

  private addNew(id: string) {
    this.seq.set(id, this.nextSeq++);
    const K = this.home.length;
    if (K === 0) { this.where.set(id, { home: false, j: this.nextArchive++ }); return; }
    let i = this.occ.indexOf(null);
    if (i === -1) {
      // Home is full: the oldest thought at home moves out into the Sky.
      let oldest = 0;
      for (let k = 1; k < K; k++) if (this.seq.get(this.occ[k]!)! < this.seq.get(this.occ[oldest]!)!) oldest = k;
      i = oldest;
      this.where.set(this.occ[i]!, { home: false, j: this.nextArchive++ });
    }
    this.occ[i] = id;
    this.where.set(id, { home: true, i });
  }

  /** Choose a spot for a thought before it is in the list (for the release animation). */
  reserve(id: string): Pt {
    if (!this.where.has(id)) { this.addNew(id); this.pending.add(id); }
    return this.spot(this.where.get(id)!);
  }

  /** Bring the placement up to date. `ids` are oldest first. */
  sync(ids: string[]): Map<string, Pt> {
    const K = this.home.length;
    if (!this.started) {
      // First layout: the same rule the step-by-step one follows, in one go.
      // Thought i (oldest first) sits at home spot i % K, or out in the Sky.
      this.started = true;
      const n = ids.length, start = Math.max(0, n - K);
      ids.forEach((id, i) => {
        this.seq.set(id, i);
        if (i < start) this.where.set(id, { home: false, j: i });
        else { const s = i % K; this.occ[s] = id; this.where.set(id, { home: true, i: s }); }
      });
      this.nextSeq = n;
      this.nextArchive = start;
    } else {
      const live = new Set(ids);
      for (const [id, w] of this.where) {
        if (live.has(id) || this.pending.has(id)) continue;
        if (w.home) this.occ[w.i] = null;
        this.where.delete(id);
        this.seq.delete(id);
      }
      for (const id of ids) {
        this.pending.delete(id);
        if (!this.where.has(id)) this.addNew(id);
      }
    }
    const out = new Map<string, Pt>();
    for (const id of ids) out.set(id, this.spot(this.where.get(id)!));
    return out;
  }

  isHome(id: string) { return !!this.where.get(id)?.home; }
}

// Order spots so that each next one has the most room from those before it.
// A part-full home then still looks evenly spread, not bunched in one corner.
function spreadOrder(pts: Pt[], centre: Pt): Pt[] {
  if (!pts.length) return [];
  const left = pts.slice();
  const dist = left.map(() => Infinity);
  let pick = 0;
  for (let i = 1; i < left.length; i++) {
    if (Math.hypot(left[i].x - centre.x, left[i].y - centre.y) < Math.hypot(left[pick].x - centre.x, left[pick].y - centre.y)) pick = i;
  }
  const out: Pt[] = [];
  while (left.length) {
    const p = left[pick];
    out.push(p);
    left.splice(pick, 1); dist.splice(pick, 1);
    pick = 0;
    for (let i = 0; i < left.length; i++) {
      dist[i] = Math.min(dist[i], Math.hypot(left[i].x - p.x, left[i].y - p.y));
      if (dist[i] > dist[pick]) pick = i;
    }
  }
  return out;
}

/** Chunks for "only draw what is near the screen". */
export const CHUNK = 360;
export const chunkKey = (cx: number, cy: number) => `${cx},${cy}`;

export function chunkIndex(spots: Map<string, Pt>): Map<string, string[]> {
  const idx = new Map<string, string[]>();
  for (const [id, p] of spots) {
    const k = chunkKey(Math.floor(p.x / CHUNK), Math.floor(p.y / CHUNK));
    const list = idx.get(k);
    if (list) list.push(id); else idx.set(k, [id]);
  }
  return idx;
}
